import React from 'react';
import { KPIGrid, KPI, KpiLabel, KpiValue } from '../ShelfPage/styles';
import BatchesEntityTable from './BatchesEntityTable';
import { DateRangeValue } from '../shared';
import axios from 'axios';
import _get from 'lodash/get';
import { useAppSelector, useSnackbar } from 'src/utils/Hooks/typedHooks';
import { buildLlmCrawlerPath } from 'src/utils/app';

export interface SummaryCampaignOverviewProps {
  batches: any[];
  isLoading: boolean;
  onBatchClick: (batchId: string) => void;
  range?: DateRangeValue;
  onChangeRange?: (value: DateRangeValue) => void;
  showDateRange?: boolean;
}

const SummaryCampaignOverview: React.FC<SummaryCampaignOverviewProps> = ({ batches, isLoading, onBatchClick, range = 'all', onChangeRange, showDateRange = true }) => {
  const userId = useAppSelector((state) => _get(state, ['user', 'session', 'userId'], null));
  const { showSnackbar } = useSnackbar();
  const getStartTimestamp = (batch: any) => {
    const ts = batch?.startedAt || batch?.startTime || batch?.createdAt || null;
    return ts ? new Date(ts).getTime() : -Infinity;
  };

  const sorted = React.useMemo(() => {
    return [...batches].sort((a, b) => getStartTimestamp(b) - getStartTimestamp(a));
  }, [batches]);

  const kpiMetrics = React.useMemo(() => {
    const toNum = (v: any): number => {
      if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
      if (typeof v === 'string') {
        const n = Number(v.replace(/,/g, ''));
        return Number.isNaN(n) ? 0 : n;
      }
      return 0;
    };
    const toSuccessPercent = (val: any): number | null => {
      if (val == null) return null;
      const raw = typeof val === 'number' ? (val > 1 ? val : val * 100) : Number(val);
      if (!Number.isFinite(raw) || raw <= 0) return null;
      return raw;
    };

    let creditsUsedSum = 0;
    let totalRowsSum = 0;
    let totalEstimatedCostSum = 0;
    let successSum = 0;
    let successCount = 0;
    (batches || []).forEach((b) => {
      creditsUsedSum += toNum(b?.actualCost ?? 0);
      const rows = toNum(b?.numRowsCollected ?? b?.rowsCollected ?? b?.rowCount ?? b?.rows ?? 0);
      totalRowsSum += rows;
      const est = toNum(b?.estimatedCost ?? b?.estimated_cost ?? 0);
      totalEstimatedCostSum += est;
      const sr = toSuccessPercent(b?.successRate ?? b?.successPercent ?? null);
      if (sr != null) {
        successSum += sr;
        successCount += 1;
      }
    });

    const avgSuccess = successCount > 0 ? Math.round(successSum / successCount) : null;
    return {
      creditsUsed: creditsUsedSum,
      totalRows: totalRowsSum,
      totalCost: creditsUsedSum,
      avgSuccessRate: avgSuccess
    } as const;
  }, [batches]);

  const getBatchId = (b: any, idx: number) => b?.id || b?.batchId || b?.crawlCampaignBatchId || b?.crawl_campaign_batch_id || idx;
  const handleDownload = async (b: any, idx: number) => {
    try {
      const bid = getBatchId(b, idx);
      if (!bid) return;
      const path = buildLlmCrawlerPath('exports?env=dev');
      await axios.post(path, { campaignBatchId: String(bid), userId });
      showSnackbar({ type: 'success', message: 'Your download is processing.' });
    } catch (e) {
      showSnackbar({ type: 'error', message: 'Failed to start download' });
    }
  };

  return (
    <>
      {showDateRange ? (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 18 }}>
            <div style={{ fontWeight: 700, fontSize: '20px' }}>Consumption</div>
          </div>
          <KPIGrid>
            <KPI>
              <KpiLabel>Credits Used</KpiLabel>
              <KpiValue style={{ fontSize: 20 }}>{Number.isFinite(kpiMetrics.creditsUsed) ? kpiMetrics.creditsUsed.toLocaleString() : '—'}</KpiValue>
            </KPI>
            <KPI>
              <KpiLabel>Total Rows</KpiLabel>
              <KpiValue style={{ fontSize: 20 }}>{Number.isFinite(kpiMetrics.totalRows) ? kpiMetrics.totalRows.toLocaleString() : '—'}</KpiValue>
            </KPI>
            <KPI>
              <KpiLabel>Total Cost</KpiLabel>
              <KpiValue style={{ fontSize: 20 }}>{Number.isFinite(kpiMetrics.totalCost) ? kpiMetrics.totalCost.toLocaleString() : '—'}</KpiValue>
            </KPI>
            <KPI>
              <KpiLabel>Success Rate</KpiLabel>
              <KpiValue style={{ fontSize: 20 }}>{kpiMetrics.avgSuccessRate != null ? `${kpiMetrics.avgSuccessRate}%` : '—'}</KpiValue>
            </KPI>
          </KPIGrid>
        </div>
      ) : null}

      <div>
        <div style={{ fontWeight: 700, fontSize: '20px', marginBottom: 16 }}>Recent Runs</div>
        <BatchesEntityTable
          batches={sorted}
          isLoading={isLoading}
          onDownload={(b, idx) => handleDownload(b, idx)}
        />
      </div>
    </>
  );
};

export default SummaryCampaignOverview;


